import type { CardTemplateId } from '@shared-core/features/backend/models'
import { formatWithThousandSeparators } from '@shared-core/utils/balances/balances'

export enum OperationStatus {
  Completed = 'completed',
  Pending = 'pending',
  Failed = 'failed',
}
export type OperationKind = 'purchase' | 'topup' | 'withdraw' | 'cancelled-purchase' | 'card-issue'

export type Operation = {
  id: string
  kind: OperationKind
  createdAt: number
  title: string
  subtitle: string
  fiatAmount: string
  billingCurrency?: string
  exchangeRate?: string
  bankId?: string
  bankName?: string
  phone?: string
  last4?: string
  description?: string
  refundNote?: string
  qrUrl?: string
  expiresAt?: number | null
  debitAmount?: string
  templateId?: CardTemplateId
  txId?: string
  cardId?: string
  status: OperationStatus
  direction: 'in' | 'out'
}

export function filterOperationsByCard(
  operations: Operation[],
  cardId?: string | null,
  cardHistory?: { templateId: CardTemplateId; cardIds: readonly string[] },
): Operation[] {
  if (cardHistory) {
    return operations.filter(
      (operation) =>
        (operation.cardId !== undefined && cardHistory.cardIds.includes(operation.cardId)) ||
        (operation.kind === 'card-issue' && operation.templateId === cardHistory.templateId),
    )
  }

  if (cardId === undefined) return operations
  return operations.filter((operation) => cardId !== null && operation.cardId === cardId)
}

// Форматируем деньги операции без вычислений через floating-point.
export function formatOperationMoney(value: string, currency: string): string {
  const formatted = formatWithThousandSeparators(value.replace(/\.00$/, '')).replace('.', ',')

  if (currency === 'USD') return `$${formatted}`

  if (currency === 'RUB') return `₽${formatted}`
  return `${formatted} ${currency}`
}

// Список и экран деталей используют один разбор полей и статусов API.
export function parseBackendOperation(data: unknown, fallback?: Operation): Operation | null {
  if (!data || typeof data !== 'object') return null
  const row = data as Record<string, unknown>
  const kindMap: Record<string, OperationKind> = {
    cardIssue: 'card-issue',
    card_issue: 'card-issue',
    topup: 'topup',
    withdraw: 'withdraw',
    purchase: 'purchase',
    purchaseReversal: 'cancelled-purchase',
  }
  const kind = typeof row.type === 'string' ? kindMap[row.type] : fallback?.kind

  if (!kind) return null
  const text = (key: string) => (typeof row[key] === 'string' ? (row[key] as string) : undefined)
  let status = fallback?.status ?? OperationStatus.Pending

  if (row.status === 'completed') status = OperationStatus.Completed

  if (row.status === 'pending' || row.status === 'processing') status = OperationStatus.Pending

  if (['failed', 'canceled', 'cancelled', 'expired'].includes(String(row.status))) status = OperationStatus.Failed

  if (kind === 'cancelled-purchase') status = OperationStatus.Failed
  const value = text('value')
  const currency = text('currency') ?? 'USD'
  let direction = fallback?.direction ?? 'out'

  if (row.direction === 'in' || row.direction === 'out') direction = row.direction
  const id = text('txHash') ?? text('txId') ?? text('id') ?? fallback?.id

  if (!id) return null
  const amount = value ? formatOperationMoney(value, currency) : (fallback?.fiatAmount ?? '—')
  const sign = direction === 'in' ? '+' : '−'
  const amountPrefix = kind === 'card-issue' ? '' : sign
  return {
    ...fallback,
    id,
    kind,
    status,
    direction,
    createdAt: typeof row.createdAt === 'number' ? row.createdAt * 1000 : (fallback?.createdAt ?? 0),
    title: text('shopName') ?? fallback?.title ?? '',
    subtitle: text('shopName') ?? text('bankName') ?? fallback?.subtitle ?? '',
    fiatAmount: value ? `${amountPrefix}${amount}` : amount,
    txId: text('txId') ?? fallback?.txId,
    cardId: text('cardId') ?? fallback?.cardId,
    templateId: row.templateId === 'platinum' || row.templateId === 'virtual' ? row.templateId : fallback?.templateId,
    debitAmount: text('billingValue') ?? fallback?.debitAmount,
    billingCurrency: text('billingCurrency') ?? fallback?.billingCurrency,
    exchangeRate: text('exchangeRate') ?? fallback?.exchangeRate,
    bankId: text('bankId') ?? fallback?.bankId,
    bankName: text('bankName') ?? fallback?.bankName,
    phone: text('phone') ?? fallback?.phone,
    last4: text('last4') ?? fallback?.last4,
    description: text('description') ?? fallback?.description,
    refundNote: text('refundNote') ?? fallback?.refundNote,
    qrUrl: text('qrUrl') ?? fallback?.qrUrl,
    expiresAt: typeof row.expiresAt === 'number' || row.expiresAt === null ? row.expiresAt : fallback?.expiresAt,
  }
}
